package org.gstechnovos.jexonplastech.sections.footer

import androidx.compose.runtime.*
import com.varabyte.kobweb.browser.dom.observers.IntersectionObserver
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.TextTransform
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.css.TransitionTimingFunction
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.gstechnovos.jexonplastech.theme.toSitePalette
import org.gstechnovos.jexonplastech.theme.unextractable
import org.gstechnovos.jexonplastech.util.LocalBreakpoint
import org.gstechnovos.jexonplastech.util.Res
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text

@Composable
private fun Modifier.footerRise(isVisible: Boolean, delayMs: Int): Modifier {
    return this.opacity(if (isVisible) 1 else 0).translateY(if (isVisible) 0.px else 30.px).transition(
        Transition.of("opacity", 0.6.s, TransitionTimingFunction.EaseOut, delayMs.ms),
        Transition.of("transform", 0.6.s, TransitionTimingFunction.EaseOut, delayMs.ms)
    )
}

@Composable
fun MainFooter(data: MainFooterData = defaultFooterData) {
    val breakpoint = LocalBreakpoint.current
    val isDesktop = breakpoint >= Res.Breakpoints.LG

    val ctx = rememberPageContext()
    val currentPath = ctx.route.path.removeSuffix("/").ifEmpty { "/" }
    var isVisible by remember { mutableStateOf(false) }

    Column(
        modifier = Modifier.fillMaxWidth().backgroundColor(Colors.White)
            .borderTop(1.px, LineStyle.Solid, ColorMode.current.toSitePalette().custom.divider)
            .padding(top = 80.px, bottom = 20.px, leftRight = 5.percent)
    ) {
        Div(attrs = Modifier.fillMaxWidth().display(DisplayStyle.Flex).flexDirection(FlexDirection.Column).toAttrs {
            ref { element ->
                val observer = IntersectionObserver { entries, obs ->
                    entries.forEach {
                        if (it.isIntersecting) {
                            isVisible = true
                            obs.unobserve(element)
                        }
                    }
                }
                observer.observe(element)
                onDispose { observer.disconnect() }
            }
        }) {
            if (isDesktop) {
                Row(
                    modifier = Modifier.fillMaxWidth().padding(bottom = 60.px),
                    horizontalArrangement = Arrangement.SpaceBetween
                ) {
                    CompanyInfoColumn(data, Modifier.footerRise(isVisible, 0))
                    data.columns.take(2).forEachIndexed { index, columnData ->
                        FooterLinkColumn(columnData, currentPath, Modifier.footerRise(isVisible, 150 + (index * 150)))
                    }
                }
            } else {
                Column(
                    modifier = Modifier.fillMaxWidth().padding(bottom = 40.px),
                    verticalArrangement = Arrangement.spacedBy(50.px)
                ) {
                    CompanyInfoColumn(data, Modifier.footerRise(isVisible, 0))

                    Row(
                        modifier = Modifier.fillMaxWidth().gap(40.px).display(DisplayStyle.Flex).flexWrap(FlexWrap.Wrap)
                    ) {
                        data.columns.forEachIndexed { index, columnData ->
                            FooterLinkColumn(
                                columnData,
                                currentPath,
                                Modifier.flex(1).minWidth(150.px).footerRise(isVisible, 150 + (index * 100))
                            )
                        }
                    }
                }
            }

            if (isDesktop) {
                Row(
                    modifier = Modifier.fillMaxWidth()
                        .borderTop(1.px, LineStyle.Solid, ColorMode.current.toSitePalette().custom.divider)
                        .padding(top = 20.px).footerRise(isVisible, 450),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    CopyrightText(data.copyrightText)
                    Row(
                        horizontalArrangement = Arrangement.spacedBy(30.px),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        LegalLinks(data.legalLinks, currentPath)
                        SocialIcons(data.socialLinks)
                    }
                }
            } else {
                Column(
                    modifier = Modifier.fillMaxWidth()
                        .borderTop(1.px, LineStyle.Solid, ColorMode.current.toSitePalette().custom.divider)
                        .padding(top = 20.px).footerRise(isVisible, 400),
                    horizontalAlignment = Alignment.CenterHorizontally,
                    verticalArrangement = Arrangement.spacedBy(20.px)
                ) {
                    SocialIcons(data.socialLinks)
                    LegalLinks(data.legalLinks, currentPath)
                    CopyrightText(data.copyrightText)
                }
            }
        }
    }
}

@Composable
private fun CompanyInfoColumn(data: MainFooterData, modifier: Modifier = Modifier) {
    val palette = ColorMode.current.toSitePalette()
    val ctx = rememberPageContext()
    Column(modifier = modifier.maxWidth(320.px)) {
        A(
            href = "/",
            attrs = Modifier
                .onClick { evt ->
                    evt.preventDefault()
                    ctx.router.navigateTo("/")
                }
                .toAttrs { style { property("text-decoration", "none") } }
        ) {
            Image(src = data.logoPath, modifier = Modifier.height(55.px).margin(bottom = 20.px))
        }
        SpanText(
            text = data.companyDescription,
            modifier = Modifier.fontSize(15.px).lineHeight(1.6).color(palette.custom.darkText).margin(bottom = 24.px)
        )
        Column(modifier = Modifier.gap(12.px)) {
            A(
                href = "tel:${data.contactPhone.replace(Regex("[^0-9+]"), "")}",
                attrs = Modifier.color(palette.custom.darkText).toAttrs {
                    style { property("text-decoration", "none") }
                }
            ) {
                Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.gap(12.px)) {
                    Image(data.phoneIconPath, Modifier.unextractable().size(16.px))
                    SpanText(data.contactPhone, modifier = Modifier.fontSize(15.px).fontWeight(FontWeight.SemiBold))
                }
            }
            A(
                href = "mailto:${data.contactEmail}",
                attrs = Modifier.color(palette.custom.darkText).toAttrs {
                    style { property("text-decoration", "none") }
                }
            ) {
                Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.gap(12.px)) {
                    Image(data.emailIconPath, Modifier.unextractable().size(16.px))
                    SpanText(data.contactEmail, modifier = Modifier.fontSize(15.px).fontWeight(FontWeight.SemiBold))
                }
            }
            Row(verticalAlignment = Alignment.Top, modifier = Modifier.gap(12.px)) {
                Image(data.locationIconPath, Modifier.unextractable().size(16.px).margin(top = 4.px))
                SpanText(
                    data.contactAddress,
                    modifier = Modifier.fontSize(15.px).lineHeight(1.5).fontWeight(FontWeight.Medium)
                )
            }
        }
    }
}

@Composable
private fun FooterLinkColumn(columnData: FooterColumnData, currentPath: String, modifier: Modifier = Modifier) {
    Column(modifier = modifier) {
        FooterTitle(columnData.title)
        Column(modifier = Modifier.gap(14.px)) {
            columnData.links.forEach { linkData ->
                val isActive = linkData.path.removeSuffix("/").ifEmpty { "/" } == currentPath
                FooterLink(linkData.title, linkData.path, isActive)
            }
        }
    }
}

@Composable
private fun FooterTitle(text: String) {
    val palette = ColorMode.current.toSitePalette()
    Column(modifier = Modifier.margin(bottom = 24.px)) {
        SpanText(
            text = text,
            modifier = Modifier.fontSize(18.px).fontWeight(FontWeight.Bold).color(palette.custom.darkText)
                .textTransform(TextTransform.Uppercase).letterSpacing(1.px).margin(bottom = 8.px)
        )
        Box(modifier = Modifier.width(30.px).height(3.px).backgroundColor(palette.brand.primaryBlue).borderRadius(2.px))
    }
}

@Composable
private fun FooterLink(text: String, path: String, isActive: Boolean = false, isSmall: Boolean = false) {
    val palette = ColorMode.current.toSitePalette()
    val ctx = rememberPageContext()

    A(
        href = path,
        attrs = AnimatedFooterLinkStyle.toModifier().fontSize(if (isSmall) 14.px else 16.px)
            .fontWeight(if (isActive) FontWeight.Bold else FontWeight.Medium)
            .then(if (isActive) Modifier.color(palette.brand.primaryBlue) else Modifier)
            .onClick { evt ->
                evt.preventDefault()
                ctx.router.navigateTo(path)
            }
            .toAttrs {
                style { property("text-decoration", "none") } // This is strictly enforced inline now!
            }
    ) {
        Text(text)
    }
}

@Composable
private fun CopyrightText(text: String) {
    SpanText(text = text, modifier = Modifier.fontSize(14.px).color(ColorMode.current.toSitePalette().custom.lightText))
}

@Composable
private fun LegalLinks(links: List<NavLinkData>, currentPath: String) {
    Row(
        modifier = Modifier.gap(20.px).flexWrap(FlexWrap.Wrap), horizontalArrangement = Arrangement.Center
    ) {
        links.forEach { linkData ->
            val isActive = linkData.path.removeSuffix("/").ifEmpty { "/" } == currentPath
            FooterLink(linkData.title, linkData.path, isActive, isSmall = true)
        }
    }
}

@Composable
private fun SocialIcons(socials: List<SocialLinkData>) {
    Row(modifier = Modifier.gap(16.px), verticalAlignment = Alignment.CenterVertically) {
        socials.forEach { social ->
            A(
                href = social.url,
                attrs = Modifier.toAttrs {
                    attr("target", "_blank")
                    style { property("text-decoration", "none") }
                }
            ) {
                Image(
                    src = social.iconPath, alt = social.altText, modifier = SocialIconStyle.toModifier().unextractable()
                )
            }
        }
    }
}

data class NavLinkData(val title: String, val path: String)
data class FooterColumnData(val title: String, val links: List<NavLinkData>)
data class SocialLinkData(val iconPath: String, val url: String, val altText: String)

data class MainFooterData(
    val logoPath: String,
    val companyDescription: String,
    val phoneIconPath: String,
    val contactPhone: String,
    val emailIconPath: String,
    val contactEmail: String,
    val locationIconPath: String,
    val contactAddress: String,
    val copyrightText: String,
    val columns: List<FooterColumnData>,
    val legalLinks: List<NavLinkData>,
    val socialLinks: List<SocialLinkData>
)

val defaultFooterData = MainFooterData(
    logoPath = Res.Image.BRAND_LOGO,
    companyDescription = "Manufacturer of all-type homeware quality products, specializing in durable cleaning brushes, wipers, brooms, and plastic baskets. Driven by a passion for excellence.",
    phoneIconPath = Res.Icons.CALL,
    contactPhone = "0281-2364168",
    emailIconPath = Res.Icons.SEND_EMAIL,
    contactEmail = "jexonplastech@gmail.com",
    locationIconPath = Res.Icons.LOCATION,
    contactAddress = "3- Patel Nagar, 80 Ft Road\nRajkot - 360002",
    copyrightText = "© 2026 Jexon Plastech. All rights reserved.",
    columns = listOf(
        FooterColumnData(
            "Quick Links", listOf(
                NavLinkData("About Us", "/about"),
                NavLinkData("Product Catalog", "/products"),
                NavLinkData("Contact Us", "/contact")
            )
        ), FooterColumnData(
            "Product Lines", listOf(
                NavLinkData("Cleaning Brushes", "/products?category=brushes"),
                NavLinkData("Floor Wipers", "/products?category=wipers"),
                NavLinkData("Heavy-Duty Brooms", "/products?category=brooms"),
                NavLinkData("Vegetable Baskets", "/products?category=baskets")
            )
        )
    ),
    legalLinks = listOf(
        NavLinkData("Privacy Policy", "/privacy-policy"), NavLinkData("Terms & Conditions", "/terms-of-service")
    ),
    socialLinks = listOf(
        SocialLinkData(Res.Icons.LINKEDIN, "https://www.linkedin.com/company/jexon-plastech/", "LinkedIn"),
        SocialLinkData(Res.Icons.FACEBOOK, "https://www.facebook.com/jexonplastech/", "Facebook")
    )
)